import { useEffect, useState } from "react";
import { CustomDataGrid } from "src/Shared/Components";
import { Button, Typography } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useStyles } from "./SalesAction.styles";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { GridPaginationModel } from "@mui/x-data-grid";
import { IPageRequest } from "src/Shared/Interfaces";
import { ISalesDealFilter } from "../../Interfaces/ISalesDealFilter";
import { useParams } from "react-router-dom";
import { IRouteParams, ISalesAction } from "../../Interfaces";
import { observer } from "mobx-react";
import { useModal } from "src/Shared/Hooks";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import AddOrEditAction from "../AddOrEditAction/AddOrEditAction";
import { assignedColumns } from "./SalesAction.column";
import AddOrEditNotes from "../AddOrEditNotes/AddOrEditNotes";
import { isClientArchived } from "../../Tools/ArchivedClient";

interface SalesActionProps {
  companyRowID?: string;
  title?: string;
}

const SalesAction: React.FC<SalesActionProps> = ({
  companyRowID: propCompanyRowID,
  title,
}) => {
  const {
    rootStore: {
      salesActionStore: {
        gridLoading,
        salesActionList,
        getSalesActionGridList,
      },
      companyStore: { companyOverview },
    },
  } = useStore();

  // Reachable at this route even with the sidebar link locked (a stale tab, a shared link, a direct
  // URL) — the write is refused server-side either way, but the button should not invite it (task
  // 5844).
  const archived = isClientArchived(companyOverview);

  const classes = useStyles();
  const modal = useModal();

  // Get from params
  const { companyRowID: paramCompanyRowID } =
    useParams<Partial<IRouteParams>>();

  // Final value (priority: prop > param)
  const companyRowID = propCompanyRowID ?? paramCompanyRowID;

  const addNewAction = () => {
    modal.open(
      <AddOrEditAction mode={VIEW_MODE.ADD} companyRowID={companyRowID} />,
    );
  };

  const [gridPagination, setGridPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [companyFilter, setCompanyFilter] = useState<ISalesDealFilter>({
    companyRowID: companyRowID,
  });

  const [request, setRequest] = useState<IPageRequest<ISalesDealFilter>>({
    pageNumber: 1,
    pageSize: 10,
    filterCollection: {
      companyRowID: companyRowID,
    },
    sortCollection: [],
  });

  const editSalesAction = (saleAction: ISalesAction) => {
    modal.open(
      <AddOrEditAction
        mode={VIEW_MODE.EDIT}
        saleAction={saleAction}
        companyRowID={companyRowID}
      />,
    );
  };

  const addNotes = (saleAction: ISalesAction) => {
    modal.open(
      <AddOrEditNotes saleAction={saleAction} companyRowID={companyRowID} />,
    );
  };

  const onGridPaginationChange = (data: GridPaginationModel) => {
    setGridPagination(data);
    setRequest({
      filterCollection: { companyRowID },
      sortCollection: [],
      pageNumber: data.page + 1,
      pageSize: data.pageSize,
    });
  };

  useEffect(() => {
    if (companyRowID) {
      getSalesActionGridList(request);
    }
  }, [request, companyRowID]);

  return (
    <>
      <div className={title ? classes.addSalesTitle : classes.addSales}>
        {title && <Typography className={classes.title} variant="h5" >{title}</Typography>}

        {!archived && (
          <Button variant="contained" onClick={addNewAction}>
            <AddCircleIcon />
            Add Action
          </Button>
        )}
      </div>

      <CustomDataGrid
        loading={gridLoading}
        columns={assignedColumns(editSalesAction, addNotes)}
        rowSelection={false}
        rows={salesActionList.results || []}
        rowCount={salesActionList.totalNumberOfRecords || 0}
        rowHeight={70}
        style={{ height: "calc(100vh - 255px)" }}
        paginationModel={gridPagination}
        getRowId={(row: ISalesAction) => row.companyActionID}
        onPaginationModelChange={(model) => onGridPaginationChange(model)}
        onRowDoubleClick={(params) => editSalesAction(params.row)}
      />
    </>
  );
};

export default observer(SalesAction);
